import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  FEATURE_KEYS_CATALOG,
  PERMISSION_ACTION_KEYS,
  PERMISSION_AREA_KEYS,
  type FeatureKey,
  type OrganizationRole,
  type PermissionAction,
  type PermissionArea,
  type PermissionScope,
  type RolesResponse,
  type TeamMember,
  type TeamRow,
} from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { canActOn, canManageRole, isSuperAdminRole } from "@gioia365/domain/access";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { rolesQuery, teamQuery, useArchiveRole, useCreateRole, useRolesRefresh, useTab, useUpdateMembership, useUpdateRole } from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Chip } from "../../design/components/Chip";
import { DataTable } from "../../design/components/DataTable";
import { Dialog } from "../../design/components/Dialog";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { SaveBar } from "../../design/components/SaveBar";
import { Select } from "../../design/components/Select";
import { TextArea } from "../../design/components/TextArea";
import { TextField } from "../../design/components/TextField";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { canCopyRole, useAccess, type Access } from "../../session/access";
import { truncateText } from "../../text";
import type { TabContext } from "../../session/tab-context";
import { ACTION_LABEL, AREA_LABEL, FEATURE_GROUPS, FEATURE_LABEL, areaActions } from "./permission-labels";
import {
  cellOf,
  cleanDescription,
  diffRole,
  draftFromRole,
  draftProblem,
  grantableFeatures,
  newRoleDraft,
  scopeLabel,
  scopeLimit,
  scopesFor,
  updateBody,
  widerThan,
  type RoleDraft,
  type RoleLevel,
} from "./role-draft";
import { joinList } from "./roles-in-words";
import "./roles.css";

// Ruoli e permessi, scheda «Ruoli» (piano M1b Task 9, mock-up `permessi.html`): a sinistra i ruoli, a destra
// l'editor. Si lavora su una bozza e si salva in una volta sola (D9): una `PATCH` per un ruolo esistente; per un
// ruolo nuovo la creazione dal ruolo di partenza, poi una `PATCH` se matrice o funzioni differiscono, poi, per il
// «Ruolo su misura», il ruolo alla persona. Se un passo si ferma il messaggio dice cosa è stato fatto.
// Decisione del product owner sulla questione 38 (30 settembre 2026): il ruolo Super admin è in sola lettura per
// tutti, e così un ruolo del suo livello (una copia scritta prima di quella regola), che nessuno copia o assegna
// perché il Super admin è uno solo; si modifica, si copia e si elimina soltanto un ruolo di livello più basso del
// proprio con poteri dentro i propri (`canCopyRole`), salvo il Super admin e la piattaforma; nessuno dà funzioni o
// ambiti che non ha. Il server controlla comunque ogni richiesta (`roles/service.ts`) e un suo rifiuto si spiega.

type Editing = { saved: RoleDraft | null; draft: RoleDraft };
/** Un avviso con il testo calcolato quando si mostra: segue la lingua dell'interfaccia. */
type Feedback = { text: (t: Translate) => string };

// Gli stessi limiti del server (`createRoleBodySchema`).
const MAX_NAME = 80;
const MAX_DESCRIPTION = 500;

const LEVEL_LABEL: Record<RoleLevel, MessageKey> = {
  1: "roles.editor.levels.user",
  2: "roles.editor.levels.superUser",
  3: "roles.editor.levels.admin",
  4: "roles.editor.levels.superAdmin",
};

const isPerson = (row: TeamRow): row is TeamMember => row.kind === "membership" && row.status !== "archived";

/** Il ruolo da cui parte «Nuovo ruolo»: il più basso fra quelli che si possono copiare, un ruolo di partenza se c'è. */
function startingRole(candidates: readonly OrganizationRole[]): OrganizationRole | undefined {
  if (candidates.length === 0) return undefined;
  const lowest = Math.min(...candidates.map((role) => role.level));
  const atLowest = candidates.filter((role) => role.level === lowest);
  return atLowest.find((role) => role.createdFromRoleId === null) ?? atLowest[0];
}

function featureName(t: Translate, key: string): string {
  const known = FEATURE_KEYS_CATALOG.find((candidate) => candidate === key);
  return known ? t(FEATURE_LABEL[known]) : key;
}

function stringsIn(details: Record<string, unknown> | undefined, name: string): string[] {
  const value = details?.[name];
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function countIn(details: Record<string, unknown> | undefined, name: string): number {
  const value = details?.[name];
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : 0;
}

/** Perché un ruolo non si elimina: con `role_in_use` quante persone e quanti inviti lo hanno ancora. */
function removalError(t: Translate, error: unknown): string {
  if (!(error instanceof ApiRequestError)) return messageForError(t, error);
  if (error.code === "role_in_use") {
    const people = countIn(error.details, "memberships");
    const invitations = countIn(error.details, "invitations");
    const parts = [
      ...(people > 0 ? [people === 1 ? t("roles.errors.peopleOne") : t("roles.errors.people", { count: people })] : []),
      ...(invitations > 0 ? [invitations === 1 ? t("roles.errors.invitationsOne") : t("roles.errors.invitations", { count: invitations })] : []),
    ];
    const [first, second] = parts;
    if (!first) return t("errors.role_in_use");
    const who = second ? t("roles.errors.both", { people: first, invitations: second }) : first;
    return people + invitations === 1 ? t("roles.errors.inUseOne", { who }) : t("roles.errors.inUse", { who });
  }
  if (error.code === "role_level" || error.code === "permission_denied") return t(`roles.errors.${error.code}`);
  return messageForError(t, error);
}

type SearchedLocation = { pathname: string; search: unknown };

export function RolesEditorTab({ suMisura }: { suMisura?: string | undefined }) {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const roles = useQuery(rolesQuery(tab));
  const team = useQuery(teamQuery(tab));

  // Una rilettura non riuscita non toglie l'editor, e con esso la bozza: l'errore conta soltanto senza dati.
  if (!roles.data || !team.data) {
    if (roles.isError || team.isError) {
      return (
        <div className="g-roles__problem">
          <Reason tone="warning" role="alert">{messageForError(t, roles.error ?? team.error)}</Reason>
          <Button
            onClick={() => {
              void roles.refetch();
              void team.refetch();
            }}
          >
            {t("common.retry")}
          </Button>
        </div>
      );
    }
    return <p className="g-roles__muted">{t("common.loading")}</p>;
  }
  return <RolesEditor tab={tab} access={access} roles={roles.data.roles} catalog={roles.data.catalog} team={team.data} suMisura={suMisura} />;
}

type EditorProps = {
  tab: TabContext;
  access: Access;
  roles: readonly OrganizationRole[];
  catalog: RolesResponse["catalog"];
  team: readonly TeamRow[];
  /** La persona del «Ruolo su misura», dall'indirizzo. */
  suMisura: string | undefined;
};

function RolesEditor({ tab, access, roles, catalog, team, suMisura }: EditorProps) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refresh = useRolesRefresh(tab);
  const createRole = useCreateRole(tab);
  const updateRole = useUpdateRole(tab);
  const updateMembership = useUpdateMembership(tab);
  const archiveRole = useArchiveRole(tab);
  const baseId = useId();
  const headingId = `${baseId}-heading`;

  const canManageRoles = access.can("manage_roles");
  // Dare il ruolo a una persona è una modifica del team (`PATCH /api/memberships/:id`).
  const canAssign = access.can("manage_team");
  const copyable = canManageRoles ? roles.filter((role) => role.archivedAt === null && canCopyRole(access, role)) : [];
  const available = new Set(catalog.features.filter((entry) => entry.availableToOrganizations).map((entry) => entry.key));
  const grantable = grantableFeatures(access.context, catalog);
  const people = team.filter(isPerson);
  const holdersOf = (roleId: string) => people.filter((person) => person.role.id === roleId).length;
  // Per eliminare conta ogni appartenenza, archiviate comprese, come nel server: fra chi ha il ruolo no.
  const membershipsWith = (roleId: string) => team.filter((row) => row.kind === "membership" && row.role.id === roleId).length;
  const invitedTo = (roleId: string) => team.filter((row) => row.kind === "invitation" && row.role.id === roleId).length;

  // «Ruolo su misura»: la bozza nasce una volta sola, dalla persona nell'indirizzo, se le regole lo permettono (le
  // stesse del pulsante nella scheda Utenti); poi la persona esce dall'indirizzo (effetto qui sotto).
  const [custom] = useState(() => {
    if (!suMisura || !canManageRoles) return null;
    const person = people.find((candidate) => candidate.membershipId === suMisura);
    const source = person ? roles.find((role) => role.id === person.role.id) : undefined;
    if (!person || !source || !canCopyRole(access, source)) return null;
    if (!canActOn(access.actor, { membershipId: person.membershipId, roleLevel: person.role.level, isProtectedOwner: person.isProtectedOwner }).ok) return null;
    const draft = newRoleDraft(
      source,
      truncateText(t("roles.editor.customName", { name: person.displayName }), MAX_NAME),
      truncateText(t("roles.editor.customDescription", { role: source.name, name: person.displayName }), MAX_DESCRIPTION),
      { membershipId: person.membershipId, displayName: person.displayName },
    );
    return { sourceId: source.id, draft };
  });
  const [selectedId, setSelectedId] = useState<string | null>(custom?.sourceId ?? null);
  const [editing, setEditing] = useState<Editing | null>(custom ? { saved: null, draft: custom.draft } : null);
  const [notice, setNotice] = useState<Feedback | null>(null);
  // Le celle che il server ha rifiutato (`validation_failed`, `details.fields`).
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(() => new Set());
  // Il nome che il server ha trovato già usato (`role_name_taken`): l'errore resta finché il nome non cambia.
  const [takenName, setTakenName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [localLeave, setLocalLeave] = useState<{ run: () => void } | null>(null);
  const [removal, setRemoval] = useState<{ role: OrganizationRole; error: Feedback | null } | null>(null);
  // Cresce a ogni bozza nuova: il nome prende il focus, già selezionato, per riscriverlo subito.
  const [focusName, setFocusName] = useState(custom ? 1 : 0);
  const nameRef = useRef<HTMLInputElement>(null);
  // Un salvataggio in corso, letto anche da un secondo clic che arriva prima del nuovo disegno.
  const savingNow = useRef(false);
  const dirtyNow = useRef(false);

  useEffect(() => {
    if (suMisura === undefined) return;
    // Ricaricare la pagina non riapre la bozza. Non è un'uscita: il dialog della bozza non serve.
    void navigate({ to: "/impostazioni/ruoli-e-permessi", search: { scheda: "ruoli" }, replace: true, ignoreBlocker: true });
  }, [suMisura, navigate]);

  useEffect(() => {
    if (focusName === 0) return;
    nameRef.current?.focus();
    nameRef.current?.select();
  }, [focusName]);

  const current = roles.find((role) => role.id === selectedId) ?? copyable[0] ?? roles[0];
  const fresh = editing !== null && editing.draft.id === null;
  const shown = fresh ? undefined : current;
  const draft = editing?.draft ?? (current ? draftFromRole(current) : null);
  const editable = draft !== null && canManageRoles && (fresh || (shown !== undefined && canCopyRole(access, shown)));
  const changes = editing ? diffRole(editing.saved, editing.draft, t) : [];
  const dirty = editable && changes.length > 0;

  useEffect(() => {
    dirtyNow.current = dirty;
  });

  // Il nome vuoto o già usato impedisce di salvare: lo dice il campo e lo ripete la barra.
  const problemCode = editing ? draftProblem(editing.draft, roles) : null;
  const takenByServer = editing !== null && takenName !== null && editing.draft.name.trim() === takenName;
  let nameError: string | undefined;
  if (problemCode === "name_missing") nameError = t("roles.editor.nameMissing");
  else if (problemCode === "name_taken" || takenByServer) nameError = t("roles.editor.nameTaken");
  const blockingProblem = nameError !== undefined;

  // Uscire con una bozza (un'altra scheda, un'altra pagina, un'altra azienda, «Esci») chiede prima (D9); lo stesso
  // indirizzo non è un'uscita. `beforeunload` vale per il ricaricamento e la chiusura della scheda del browser.
  const shouldBlockFn = useCallback(
    ({ current: from, next }: { current: SearchedLocation; next: SearchedLocation }) =>
      dirtyNow.current && !(from.pathname === next.pathname && JSON.stringify(from.search) === JSON.stringify(next.search)),
    [],
  );
  const enableBeforeUnload = useCallback(() => dirtyNow.current, []);
  const blocker = useBlocker({ shouldBlockFn, enableBeforeUnload, withResolver: true });

  function clearFeedback() {
    setNotice(null);
    setInvalid(new Set());
    setTakenName(null);
  }

  /** Un'azione che butterebbe la bozza passa prima dal dialog. */
  function guarded(run: () => void) {
    if (dirty) setLocalLeave({ run });
    else run();
  }

  function openRole(role: OrganizationRole) {
    if (!fresh && role.id === current?.id) return;
    guarded(() => {
      setEditing(null);
      setSelectedId(role.id);
      clearFeedback();
    });
  }

  function startDraft(from: OrganizationRole, name: string, description: string) {
    guarded(() => {
      setEditing({ saved: null, draft: newRoleDraft(from, truncateText(name, MAX_NAME), truncateText(description, MAX_DESCRIPTION)) });
      clearFeedback();
      setFocusName((count) => count + 1);
    });
  }

  function change(update: (next: RoleDraft) => RoleDraft) {
    if (!draft || !editable || saving) return;
    const base = editing ?? { saved: draft, draft };
    setEditing({ saved: base.saved, draft: update(base.draft) });
  }

  /** Un campo segnato dal server (`validation_failed`) torna pulito appena si cambia. */
  function clearInvalid(field: string) {
    if (invalid.has(field)) setInvalid(new Set([...invalid].filter((candidate) => candidate !== field)));
  }

  function changeCell(area: PermissionArea, action: PermissionAction, scope: PermissionScope) {
    change((next) => ({ ...next, areaPermissions: { ...next.areaPermissions, [area]: { ...next.areaPermissions[area], [action]: scope } } }));
    clearInvalid(`areaPermissions.${area}.${action}`);
  }

  function toggleFeature(key: FeatureKey, checked: boolean) {
    change((next) => ({ ...next, features: checked ? [...next.features, key] : next.features.filter((candidate) => candidate !== key) }));
  }

  function discard() {
    setEditing(null);
    clearFeedback();
    toast.show(t("roles.saveBar.discarded"));
  }

  /** La risposta del server prende subito il posto del ruolo nella cache; la rilettura la conferma. */
  function remember(role: OrganizationRole) {
    queryClient.setQueryData(rolesQuery(tab).queryKey, (data) =>
      data && {
        ...data,
        roles: data.roles.some((candidate) => candidate.id === role.id)
          ? data.roles.map((candidate) => (candidate.id === role.id ? role : candidate))
          : [...data.roles, role],
      },
    );
  }

  function settle(roleId: string) {
    setSelectedId(roleId);
    setEditing(null);
  }

  /** Un rifiuto del server, spiegato dove è successo: il nome, le celle, oppure l'avviso sopra la barra. */
  function explain(error: unknown, name: string) {
    if (!(error instanceof ApiRequestError)) {
      setNotice({ text: (tr) => messageForError(tr, error) });
      return;
    }
    switch (error.code) {
      case "role_name_taken":
        setTakenName(name);
        nameRef.current?.focus();
        return;
      case "validation_failed":
        setInvalid(new Set(stringsIn(error.details, "fields")));
        setNotice({ text: (tr) => tr("roles.errors.validation_failed") });
        return;
      case "feature_unavailable": {
        const features = stringsIn(error.details, "features");
        setNotice({ text: (tr) => tr("roles.errors.feature_unavailable", { list: joinList(features.map((key) => featureName(tr, key))) }) });
        return;
      }
      case "permission_denied":
      case "role_level":
        setNotice({ text: (tr) => tr(`roles.errors.${error.code === "role_level" ? "role_level" : "permission_denied"}`) });
        return;
      case "system_role":
        setNotice({ text: (tr) => tr("roles.editor.superAdminLevel") });
        return;
      default:
        setNotice({ text: (tr) => messageForError(tr, error) });
    }
  }

  async function save() {
    if (!editing || savingNow.current || blockingProblem) return;
    const { saved, draft: next } = editing;
    const count = changes.length;
    const name = next.name.trim();
    savingNow.current = true;
    setSaving(true);
    clearFeedback();
    try {
      let result: { id: string; name: string };
      if (saved === null || next.id === null) {
        // 1. La creazione copia livello, permessi e funzioni dal ruolo di partenza.
        let created: OrganizationRole;
        try {
          const description = cleanDescription(next.description);
          created = (await createRole.mutateAsync({ name, ...(description ? { description } : {}), copyFromRoleId: next.copyFromRoleId })).role;
        } catch (error) {
          explain(error, name);
          return;
        }
        remember(created);
        result = created;
        // 2. Il completamento, soltanto se matrice o funzioni differiscono dal ruolo creato.
        const createdDraft = draftFromRole(created);
        const completion = updateBody(createdDraft, { ...next, name: createdDraft.name, description: createdDraft.description }, available);
        if (completion) {
          try {
            const completed = (await updateRole.mutateAsync({ roleId: created.id, body: completion })).role;
            remember(completed);
            result = completed;
          } catch {
            // Il ruolo esiste: la bozza diventa una modifica del ruolo creato, e «Salva il ruolo» riprende da qui.
            setSelectedId(created.id);
            setEditing({ saved: createdDraft, draft: { ...next, id: created.id } });
            setNotice({ text: (tr) => tr("roles.saveBar.notCompleted", { name: created.name }) });
            return;
          }
        }
      } else {
        const body = updateBody(saved, next, available);
        result = { id: next.id, name };
        if (body) {
          try {
            const updated = (await updateRole.mutateAsync({ roleId: next.id, body })).role;
            remember(updated);
            result = updated;
          } catch (error) {
            explain(error, name);
            return;
          }
        }
      }
      // 3. «Ruolo su misura»: il ruolo alla persona, se chi salva gestisce anche il team.
      const person = next.assignTo;
      if (person && canAssign) {
        try {
          await updateMembership.mutateAsync({ membershipId: person.membershipId, body: { roleId: result.id } });
        } catch {
          const role = result;
          settle(role.id);
          setNotice({ text: (tr) => tr("roles.saveBar.notGiven", { name: role.name, person: person.displayName }) });
          return;
        }
      }
      settle(result.id);
      toast.show(count === 1 ? t("roles.saveBar.savedOne", { name: result.name }) : t("roles.saveBar.saved", { name: result.name, count }), "success");
    } finally {
      savingNow.current = false;
      setSaving(false);
      refresh();
    }
  }

  async function confirmRemoval() {
    if (!removal || archiveRole.isPending) return;
    const target = removal.role;
    try {
      await archiveRole.mutateAsync(target.id);
      queryClient.setQueryData(rolesQuery(tab).queryKey, (data) => data && { ...data, roles: data.roles.filter((role) => role.id !== target.id) });
      // La conferma si chiude prima dell'avviso (D16).
      setRemoval(null);
      setEditing(null);
      setSelectedId(null);
      clearFeedback();
      toast.show(t("roles.remove.done", { name: target.name }), "success");
    } catch (error) {
      setRemoval({ role: target, error: { text: (tr) => removalError(tr, error) } });
    } finally {
      refresh();
    }
  }

  function stay() {
    if (blocker.status === "blocked") blocker.reset();
    setLocalLeave(null);
  }

  function leave() {
    setEditing(null);
    clearFeedback();
    if (blocker.status === "blocked") {
      blocker.proceed();
      return;
    }
    const pending = localLeave;
    setLocalLeave(null);
    pending?.run();
  }

  const newSource = startingRole(copyable);
  const draftName = draft ? draft.name.trim() || t("roles.editor.newName") : "";

  let holders = "";
  if (draft?.assignTo) {
    holders = canAssign
      ? t("roles.editor.forPerson", { name: draft.assignTo.displayName })
      : t("roles.editor.forPersonNoTeam", { name: draft.assignTo.displayName });
  } else if (fresh) {
    holders = t("roles.editor.unsaved");
  } else if (shown) {
    const count = holdersOf(shown.id);
    if (count === 0) holders = t("roles.editor.holdersNone");
    else if (count === 1) holders = t("roles.editor.holdersOne");
    else holders = t("roles.editor.holders", { count });
  }

  // Il ruolo di sistema ha il suo avviso con il lucchetto; un altro ruolo del suo livello lo spiega per tutti.
  let readOnlyReason: string | null = null;
  if (shown && !shown.isSystem && isSuperAdminRole(shown)) {
    readOnlyReason = t("roles.editor.superAdminLevel");
  } else if (canManageRoles && shown && !shown.isSystem && !editable) {
    readOnlyReason = canManageRole(access.actor, shown) ? t("roles.editor.powersReadOnly") : t("roles.editor.levelReadOnly");
  }
  const canDuplicate = canManageRoles && shown !== undefined && canCopyRole(access, shown);
  const canRemove = editable && shown !== undefined && membershipsWith(shown.id) === 0 && invitedTo(shown.id) === 0;

  let summary = "";
  if (dirty) {
    const firstTwo = changes.slice(0, 2).join(" ");
    const listed = changes.length > 2 ? t("roles.saveBar.more", { changes: firstTwo }) : firstTwo;
    summary =
      changes.length === 1
        ? t("roles.saveBar.summaryOne", { name: draftName, changes: listed })
        : t("roles.saveBar.summary", { count: changes.length, name: draftName, changes: listed });
  }

  function cell(area: PermissionArea, action: PermissionAction) {
    if (!draft) return null;
    if (!areaActions(area).includes(action)) {
      return (
        <>
          <span aria-hidden="true">—</span>
          <span className="g-visually-hidden">{t("roles.editor.notNeeded")}</span>
        </>
      );
    }
    const value = cellOf(draft.areaPermissions, area, action);
    if (!editable) return scopeLabel(t, action, value);
    const limit = scopeLimit(access.context, area, action);
    const field = `areaPermissions.${area}.${action}`;
    return (
      <Select
        id={`${baseId}-${area}-${action}`}
        label={t("roles.editor.cell", { area: t(AREA_LABEL[area]), action: t(ACTION_LABEL[action]) })}
        hideLabel
        className="g-roles__cell"
        value={value}
        onChange={(next) => {
          const scope = scopesFor(action).find((candidate) => candidate === next);
          if (scope) changeCell(area, action, scope);
        }}
        options={scopesFor(action).map((scope) => {
          // Più largo di ciò che ha chi modifica: resta visibile, spento, con il motivo (questione 38).
          const beyond = widerThan(scope, limit);
          const label = scopeLabel(t, action, scope);
          return { value: scope, label: beyond ? t("roles.editor.notYoursOption", { scope: label }) : label, disabled: beyond };
        })}
        error={invalid.has(field) ? t("roles.editor.invalidCell") : undefined}
        disabled={saving}
      />
    );
  }

  function feature(key: FeatureKey) {
    if (!draft) return null;
    // Il ruolo di sistema ha ogni funzione, anche quelle spente per le aziende.
    const off = shown?.isSystem !== true && !available.has(key);
    const notYours = editable && !off && !grantable.has(key);
    let hint: string | undefined;
    if (off) hint = t("roles.editor.unavailable");
    else if (notYours) hint = t("roles.editor.notYours");
    return (
      <Checkbox
        key={key}
        id={`${baseId}-feature-${key}`}
        label={t(FEATURE_LABEL[key])}
        checked={!off && draft.features.includes(key)}
        disabled={!editable || off || notYours || saving}
        hint={hint}
        onChange={(checked) => toggleFeature(key, checked)}
      />
    );
  }

  return (
    <>
      <div className="g-roles__editor">
        <nav className="g-roles__list" aria-label={t("roles.list.label")}>
          <ul>
            {roles.map((role) => {
              const count = holdersOf(role.id);
              return (
                <li key={role.id}>
                  <button type="button" className="g-roles__item" aria-pressed={!fresh && role.id === current?.id} onClick={() => openRole(role)}>
                    <strong>{role.name}</strong>
                    {role.isSystem ? <Chip>{t("roles.list.system")}</Chip> : null}
                    <small>{count === 1 ? t("roles.list.holdersOne") : t("roles.list.holders", { count })}</small>
                  </button>
                </li>
              );
            })}
            {fresh ? (
              <li>
                <button type="button" className="g-roles__item" aria-pressed="true">
                  <strong>{draftName}</strong>
                  <small>{t("roles.list.unsaved")}</small>
                </button>
              </li>
            ) : null}
          </ul>
          {newSource ? (
            <Button
              onClick={() => startDraft(newSource, t("roles.editor.newName"), t("roles.editor.newDescription", { role: newSource.name }))}
            >
              <Icon name="plus" />
              {t("roles.list.add")}
            </Button>
          ) : null}
        </nav>

        {draft ? (
          <section className="g-roles__edit" aria-labelledby={headingId}>
            <div className="g-roles__edit-head">
              <div className="g-roles__edit-title">
                <h2 id={headingId} className={editable ? "g-visually-hidden" : undefined}>
                  {editable ? (shown?.name ?? draftName) : draft.name}
                </h2>
                {editable ? (
                  <>
                    <TextField
                      ref={nameRef}
                      id={`${baseId}-name`}
                      label={t("roles.editor.name")}
                      value={draft.name}
                      maxLength={MAX_NAME}
                      autoComplete="off"
                      error={nameError ?? (invalid.has("name") ? t("roles.editor.invalidText") : undefined)}
                      disabled={saving}
                      onChange={(event) => {
                        const name = event.target.value;
                        change((next) => ({ ...next, name }));
                        clearInvalid("name");
                      }}
                    />
                    <TextArea
                      id={`${baseId}-description`}
                      label={t("roles.editor.description")}
                      value={draft.description ?? ""}
                      maxLength={MAX_DESCRIPTION}
                      autoComplete="off"
                      hint={t("roles.editor.descriptionHint")}
                      error={invalid.has("description") ? t("roles.editor.invalidText") : undefined}
                      disabled={saving}
                      onChange={(event) => {
                        const description = event.target.value;
                        change((next) => ({ ...next, description }));
                        clearInvalid("description");
                      }}
                    />
                  </>
                ) : draft.description ? (
                  <p className="g-roles__muted g-roles__description">{draft.description}</p>
                ) : null}
                <p className="g-roles__level-line">{t("roles.editor.level", { level: t(LEVEL_LABEL[draft.level]) })}</p>
                <p className="g-roles__muted">{t("roles.editor.levelHelp")}</p>
                <p className="g-roles__muted">{holders}</p>
              </div>
              {canDuplicate || canRemove ? (
                <div className="g-roles__edit-actions">
                  {canDuplicate && shown ? (
                    <Button
                      onClick={() => startDraft(shown, t("roles.editor.copyName", { name: shown.name }), t("roles.editor.copyDescription", { role: shown.name }))}
                    >
                      {t("roles.editor.duplicate")}
                    </Button>
                  ) : null}
                  {canRemove && shown ? (
                    <Button variant="ghost" onClick={() => setRemoval({ role: shown, error: null })}>
                      {t("roles.editor.delete")}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {shown?.isSystem ? (
              <p className="g-roles__lock">
                <Icon name="lock" />
                <span>{t("roles.editor.systemLock")}</span>
              </p>
            ) : null}
            {readOnlyReason ? <Reason>{readOnlyReason}</Reason> : null}

            <DataTable caption={t("roles.editor.matrixCaption")} captionHidden>
              <thead>
                <tr>
                  <th scope="col">{t("roles.editor.matrixArea")}</th>
                  {PERMISSION_ACTION_KEYS.map((action) => (
                    <th key={action} scope="col">{t(ACTION_LABEL[action])}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_AREA_KEYS.map((area) => (
                  <tr key={area}>
                    <th scope="row">{t(AREA_LABEL[area])}</th>
                    {PERMISSION_ACTION_KEYS.map((action) => (
                      <td key={action}>{cell(area, action)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </DataTable>
            <p className="g-roles__muted">{t("roles.editor.matrixNote")}</p>

            <div className="g-roles__features">
              {FEATURE_GROUPS.map((group) => (
                <fieldset key={group.title} className="g-roles__group">
                  <legend>{t(group.title)}</legend>
                  {group.title === "permissions.group.settings" ? <p className="g-roles__muted">{t("roles.editor.settingsNote")}</p> : null}
                  {group.keys.map(feature)}
                </fieldset>
              ))}
            </div>

            {notice ? (
              <Reason tone="warning" role="alert">
                {notice.text(t)}
              </Reason>
            ) : null}
          </section>
        ) : null}
      </div>

      {dirty ? (
        <SaveBar summary={summary} problem={nameError} onDiscard={discard} onSave={() => void save()} saveLabel={t("roles.saveBar.save")} saving={saving} />
      ) : null}

      {blocker.status === "blocked" || localLeave ? (
        <Dialog
          open
          title={t("roles.leave.title")}
          onClose={stay}
          footer={
            <>
              <Button onClick={leave}>{t("roles.leave.discard")}</Button>
              <Button variant="primary" onClick={stay}>
                {t("roles.leave.stay")}
              </Button>
            </>
          }
        >
          <p>{t("roles.leave.text")}</p>
        </Dialog>
      ) : null}

      {removal ? (
        <Dialog
          open
          title={t("roles.remove.title", { name: removal.role.name })}
          onClose={() => setRemoval(null)}
          footer={
            <>
              <Button onClick={() => setRemoval(null)}>{t("common.cancel")}</Button>
              <Button
                variant="primary"
                onClick={() => void confirmRemoval()}
                disabled={archiveRole.isPending}
                aria-busy={archiveRole.isPending ? "true" : undefined}
              >
                {t("roles.remove.confirm")}
              </Button>
            </>
          }
        >
          <p>{t("roles.remove.text")}</p>
          {removal.error ? (
            <Reason tone="warning" role="alert">
              {removal.error.text(t)}
            </Reason>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}
